<script setup lang="ts">
    import type { AgentWebSearch } from 'models'
    import { WebSearchProvider } from 'models'
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'
    import PkCheckboxField from '../PkCheckboxField.vue'

    const { t: $t } = useI18n({ useScope: 'global' })

    defineProps<{
        activeProvider: string
        disabled?: boolean
    }>()

    const webSearch = defineModel<AgentWebSearch>('webSearch', {
        default: () => ({
            enabled: false,
            provider: WebSearchProvider.Native,
        }),
    })

    /** Perplexity searchDomainFilter as comma-separated string */
    const perplexityDomainFilter = computed({
        get: () =>
            webSearch.value?.perplexity?.searchDomainFilter?.join(', ') ?? '',
        set: (val: string) => {
            const domains = val
                .split(',')
                .map((d) => d.trim())
                .filter(Boolean)
            webSearch.value = {
                ...webSearch.value,
                perplexity: {
                    ...webSearch.value?.perplexity,
                    searchDomainFilter: domains.length ? domains : undefined,
                },
            }
        },
    })

    /** Parallel includeDomains as comma-separated string */
    const parallelIncludeDomains = computed({
        get: () =>
            webSearch.value?.parallel?.sourcePolicy?.includeDomains?.join(
                ', ',
            ) ?? '',
        set: (val: string) => {
            const domains = val
                .split(',')
                .map((d) => d.trim())
                .filter(Boolean)
            webSearch.value = {
                ...webSearch.value,
                parallel: {
                    ...webSearch.value?.parallel,
                    sourcePolicy: {
                        ...webSearch.value?.parallel?.sourcePolicy,
                        includeDomains: domains.length ? domains : undefined,
                    },
                },
            }
        },
    })

    /** Parallel excludeDomains as comma-separated string */
    const parallelExcludeDomains = computed({
        get: () =>
            webSearch.value?.parallel?.sourcePolicy?.excludeDomains?.join(
                ', ',
            ) ?? '',
        set: (val: string) => {
            const domains = val
                .split(',')
                .map((d) => d.trim())
                .filter(Boolean)
            webSearch.value = {
                ...webSearch.value,
                parallel: {
                    ...webSearch.value?.parallel,
                    sourcePolicy: {
                        ...webSearch.value?.parallel?.sourcePolicy,
                        excludeDomains: domains.length ? domains : undefined,
                    },
                },
            }
        },
    })

    /** Anthropic allowedDomains as comma-separated string */
    const anthropicAllowedDomains = computed({
        get: () =>
            webSearch.value?.native?.anthropic?.allowedDomains?.join(', ') ??
            '',
        set: (val: string) => {
            const domains = val
                .split(',')
                .map((d) => d.trim())
                .filter(Boolean)
            webSearch.value = {
                ...webSearch.value,
                native: {
                    ...webSearch.value?.native,
                    anthropic: {
                        ...webSearch.value?.native?.anthropic,
                        allowedDomains: domains.length ? domains : undefined,
                    },
                },
            }
        },
    })

    /** Anthropic blockedDomains as comma-separated string */
    const anthropicBlockedDomains = computed({
        get: () =>
            webSearch.value?.native?.anthropic?.blockedDomains?.join(', ') ??
            '',
        set: (val: string) => {
            const domains = val
                .split(',')
                .map((d) => d.trim())
                .filter(Boolean)
            webSearch.value = {
                ...webSearch.value,
                native: {
                    ...webSearch.value?.native,
                    anthropic: {
                        ...webSearch.value?.native?.anthropic,
                        blockedDomains: domains.length ? domains : undefined,
                    },
                },
            }
        },
    })
</script>

<template>
    <PkCheckboxField
        :model-value="webSearch?.enabled ?? false"
        name="webSearchEnabled"
        switch
        :disabled
        :label="$t('label.webSearchEnabled')"
        :hint-label="$t('hint.webSearchEnabled')"
        @update:model-value="webSearch = { ...webSearch, enabled: $event }" />
    <template v-if="webSearch?.enabled">
        <VvCombobox
            :model-value="webSearch?.provider ?? 'perplexity'"
            name="webSearchProvider"
            select-hint-label=""
            deselect-hint-label=""
            selected-hint-label=""
            icon="ri:search-line"
            strategy="fixed"
            :label="$t('label.webSearchProvider')"
            :placeholder="$t('placeholder.select')"
            :unselectable="false"
            :options="[
                { label: 'Perplexity', value: 'perplexity' },
                { label: 'Parallel', value: 'parallel' },
                { label: $t('label.webSearchNative'), value: 'native' },
            ]"
            class="mb-0"
            @update:model-value="
                webSearch = { ...webSearch, provider: $event }
            " />
        <!-- Perplexity config -->
        <template v-if="webSearch?.provider === 'perplexity'">
            <VvInputText
                :model-value="webSearch?.perplexity?.maxResults"
                name="webSearch.perplexity.maxResults"
                type="number"
                :min="1"
                :max="20"
                :label="$t('label.maxResults')"
                :placeholder="'10'"
                :disabled
                class="mt-md"
                @update:model-value="
                    webSearch = {
                        ...webSearch,
                        perplexity: {
                            ...webSearch?.perplexity,
                            maxResults: $event ? Number($event) : undefined,
                        },
                    }
                " />
            <VvCombobox
                :model-value="webSearch?.perplexity?.searchRecencyFilter"
                name="webSearch.perplexity.searchRecencyFilter"
                select-hint-label=""
                deselect-hint-label=""
                strategy="fixed"
                :label="$t('label.searchRecencyFilter')"
                :placeholder="$t('placeholder.select')"
                :options="[
                    { label: $t('label.day'), value: 'day' },
                    { label: $t('label.week'), value: 'week' },
                    { label: $t('label.month'), value: 'month' },
                    { label: $t('label.year'), value: 'year' },
                ]"
                :disabled
                @update:model-value="
                    webSearch = {
                        ...webSearch,
                        perplexity: {
                            ...webSearch?.perplexity,
                            searchRecencyFilter: $event,
                        },
                    }
                " />
            <VvInputText
                :model-value="webSearch?.perplexity?.country"
                name="webSearch.perplexity.country"
                :label="$t('label.country')"
                :placeholder="'US'"
                @update:model-value="
                    webSearch = {
                        ...webSearch,
                        perplexity: {
                            ...webSearch?.perplexity,
                            country: $event || undefined,
                        },
                    }
                " />
            <VvInputText
                v-model="perplexityDomainFilter"
                name="webSearch.perplexity.searchDomainFilter"
                :label="$t('label.domainFilter')"
                :placeholder="'reuters.com, bbc.com, -reddit.com'"
                :hint-label="$t('hint.allowedDomains')"
                :disabled
                class="mb-0" />
        </template>
        <!-- Parallel config -->
        <template v-else-if="webSearch?.provider === 'parallel'">
            <VvCombobox
                :model-value="webSearch?.parallel?.mode"
                name="webSearch.parallel.mode"
                select-hint-label=""
                deselect-hint-label=""
                strategy="fixed"
                :label="$t('label.searchMode')"
                :placeholder="$t('placeholder.select')"
                :options="[
                    { label: 'One-shot', value: 'one-shot' },
                    { label: 'Agentic', value: 'agentic' },
                ]"
                :disabled
                class="mt-md"
                @update:model-value="
                    webSearch = {
                        ...webSearch,
                        parallel: { ...webSearch?.parallel, mode: $event },
                    }
                " />
            <VvInputText
                :model-value="webSearch?.parallel?.maxResults"
                name="webSearch.parallel.maxResults"
                type="number"
                :min="1"
                :max="20"
                :label="$t('label.maxResults')"
                :placeholder="'10'"
                :disabled
                @update:model-value="
                    webSearch = {
                        ...webSearch,
                        parallel: {
                            ...webSearch?.parallel,
                            maxResults: $event ? Number($event) : undefined,
                        },
                    }
                " />
            <VvInputText
                v-model="parallelIncludeDomains"
                name="webSearch.parallel.includeDomains"
                :label="$t('label.includeDomains')"
                :placeholder="'arxiv.org, nature.com'"
                :hint-label="$t('hint.allowedDomains')"
                :disabled />
            <VvInputText
                v-model="parallelExcludeDomains"
                name="webSearch.parallel.excludeDomains"
                :label="$t('label.excludeDomains')"
                :placeholder="'reddit.com, quora.com'"
                :hint-label="$t('hint.blockedDomains')"
                :disabled
                class="mb-0" />
        </template>
        <!-- Native config -->
        <template v-else-if="webSearch?.provider === 'native'">
            <template v-if="activeProvider === 'anthropic'">
                <VvInputText
                    :model-value="webSearch?.native?.anthropic?.maxUses"
                    name="webSearch.native.anthropic.maxUses"
                    type="number"
                    :min="1"
                    :label="$t('label.maxUses')"
                    :placeholder="$t('placeholder.insert')"
                    :disabled
                    class="mt-md"
                    @update:model-value="
                        webSearch = {
                            ...webSearch,
                            native: {
                                ...webSearch?.native,
                                anthropic: {
                                    ...webSearch?.native?.anthropic,
                                    maxUses: $event
                                        ? Number($event)
                                        : undefined,
                                },
                            },
                        }
                    " />
                <VvInputText
                    v-model="anthropicAllowedDomains"
                    name="webSearch.native.anthropic.allowedDomains"
                    :label="$t('label.allowedDomains')"
                    :placeholder="'techcrunch.com, wired.com'"
                    :hint-label="$t('hint.allowedDomains')"
                    :disabled />
                <VvInputText
                    v-model="anthropicBlockedDomains"
                    name="webSearch.native.anthropic.blockedDomains"
                    :label="$t('label.blockedDomains')"
                    :placeholder="'spam-site.com'"
                    :hint-label="$t('hint.blockedDomains')"
                    :disabled
                    class="mb-0" />
            </template>
        </template>
    </template>
</template>
